<template>
    <div class="log-page">
        <section class="log-hero">
            <div class="log-hero__copy">
                <div class="log-hero__eyebrow">{{ $tt('操作中心') }}</div>
                <h1>{{ $tt('查看操作记录和运行日志') }}</h1>
                <p>{{ $tt('左侧切换日志类型，右侧查看当前筛选结果和运行输出，保持同一套安静的深色圆角面板。') }}</p>
            </div>
            <div class="log-hero__stats">
                <div class="log-hero__stat">
                    <span>{{ $tt('当前视图') }}</span>
                    <strong>{{ activeModeTitle }}</strong>
                    <small>{{ activeModeHint }}</small>
                </div>
                <div class="log-hero__stat">
                    <span>{{ $tt('状态摘要') }}</span>
                    <strong>{{ summaryPrimaryText }}</strong>
                    <small>{{ summarySecondaryText }}</small>
                </div>
                <div class="log-hero__stat">
                    <span>{{ $tt('筛选范围') }}</span>
                    <strong>{{ summaryTertiaryText }}</strong>
                    <small>{{ runtimeSourceHint }}</small>
                </div>
            </div>
        </section>
        <div class="log-shell">
            <aside class="log-sidebar">
                <div class="log-mode-switch" role="tablist">
                    <button
                        class="log-mode-switch__item"
                        :class="{ 'is-active': tabSelect2 === 'log' }"
                        type="button"
                        @click="handleTab2('log')"
                    >{{ $tt('操作日志') }}</button>
                    <button
                        v-if="getUserRole === 'V2'"
                        class="log-mode-switch__item"
                        :class="{ 'is-active': tabSelect2 === 'runtime' }"
                        type="button"
                        @click="handleTab2('runtime')"
                    >{{ $tt('运行日志') }}</button>
                </div>

                <div v-if="tabSelect2 === 'log'" class="log-nav-list">
                    <button
                        v-for="action in visibleActionOptions"
                        :key="action.key"
                        class="log-nav-row"
                        :class="{ 'is-active': currentActionKey === action.key }"
                        type="button"
                        @click="selectAction(action.key)"
                    >
                        <span class="log-nav-row__main">
                            <span class="log-nav-row__label">{{ $tt(action.label) }}</span>
                            <span class="log-nav-row__badge" :class="softBadgeClass(action.tone)">{{ $tt(action.badge) }}</span>
                        </span>
                        <span class="log-nav-row__count">{{ getActionCountValue(action.key) }}</span>
                    </button>
                </div>

                <div v-else class="log-nav-list">
                    <button
                        v-for="runtime in runtimeOptions"
                        :key="runtime.value"
                        class="log-nav-row"
                        :class="{ 'is-active': tabSelectRuntime === runtime.value }"
                        type="button"
                        @click="selectRuntime(runtime.value)"
                    >
                        <span class="log-nav-row__main">
                            <span class="log-nav-row__label">{{ runtime.label }}</span>
                            <span class="log-nav-row__badge" :class="softBadgeClass(runtime.tone)">{{ $tt(runtime.badge) }}</span>
                        </span>
                    </button>
                </div>
            </aside>

            <main class="log-main">
                <section v-if="tabSelect2 === 'log'" class="log-panel">
                    <header class="log-toolbar">
                        <div class="log-toolbar__copy">
                            <h2>{{ activeActionLabel }}</h2>
                            <p>{{ activeActionDescription }}</p>
                        </div>
                        <div class="log-toolbar__stats">
                            <div class="log-stat">
                                <span>{{ $tt('记录') }}</span>
                                <strong>{{ totalRecords }}</strong>
                            </div>
                            <div class="log-stat">
                                <span>{{ $tt('本页') }}</span>
                                <strong>{{ auditLogData.length }}</strong>
                            </div>
                            <div class="log-stat">
                                <span>{{ $tt('页码') }}</span>
                                <strong>{{ currentPage }}</strong>
                            </div>
                        </div>
                    </header>

                    <div class="log-filter-bar">
                        <label class="log-field">
                            <span>{{ $tt('日志等级') }}</span>
                            <select class="select select-sm log-control" v-model="levelFilter" @change="handleFilterChange">
                                <option value="">{{ $tt('全部等级') }}</option>
                                <option value="info">{{ $tt('信息') }}</option>
                                <option value="warning">{{ $tt('警告') }}</option>
                                <option value="error">{{ $tt('错误') }}</option>
                            </select>
                        </label>

                        <label class="log-field">
                            <span>{{ $tt('状态') }}</span>
                            <select class="select select-sm log-control" v-model="statusFilter" @change="handleFilterChange">
                                <option value="">{{ $tt('全部状态') }}</option>
                                <option value="running">{{ $tt('进行中') }}</option>
                                <option value="success">{{ $tt('成功') }}</option>
                                <option value="stop">{{ $tt('停止') }}</option>
                                <option value="wait">{{ $tt('等待') }}</option>
                                <option value="failed">{{ $tt('失败') }}</option>
                            </select>
                        </label>

                        <label class="log-field log-field--wide">
                            <span>{{ $tt('用户') }}</span>
                            <input
                                type="text"
                                class="input input-sm log-control"
                                :placeholder="$tt('搜索用户')"
                                v-model="userFilter"
                                @input="handleFilterChange"
                            >
                        </label>

                        <div class="log-filter-actions">
                            <button class="btn btn-sm btn-outline whitespace-nowrap" type="button" @click="resetFilters">
                                {{ $tt('清空筛选') }}
                            </button>
                            <button class="btn btn-sm btn-error btn-outline whitespace-nowrap" type="button" @click="handleDeleteAuditLogs">
                                {{ $tt('删除日志') }}
                            </button>
                        </div>
                    </div>

                    <div v-if="auditLogError" class="log-state log-state--error">
                        <strong>{{ $tt('提交失败') }}</strong>
                        <span>{{ auditLogError }}</span>
                    </div>

                    <div v-else-if="isAuditLoading" class="log-list-skeleton" aria-live="polite">
                        <div v-for="index in 8" :key="index" class="log-skeleton-row"></div>
                    </div>

                    <div v-else-if="auditLogData.length === 0" class="log-state">
                        <strong>{{ $tt('暂无操作日志') }}</strong>
                        <span>{{ $tt('当前筛选条件下没有找到相关日志记录') }}</span>
                    </div>

                    <div v-else class="log-table-wrap">
                        <div class="overflow-x-auto">
                            <table class="table log-table">
                                <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{{ $tt('用户') }}</th>
                                    <th>{{ $tt('操作') }}</th>
                                    <th>{{ $tt('等级') }}</th>
                                    <th>{{ $tt('状态') }}</th>
                                    <th>{{ $tt('详情摘要') }}</th>
                                    <th>{{ $tt('时间') }}</th>
                                    <th>{{ $tt('操作') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="item in auditLogData"
                                    :key="item.id"
                                    class="log-table__row"
                                    @click="openLogDrawer(item)"
                                >
                                    <td class="font-mono">#{{ item.id }}</td>
                                    <td>
                                        <span class="log-user">{{ item.user || '-' }}</span>
                                    </td>
                                    <td>
                                        <span class="log-chip log-chip--outline">{{ $tt(item.action || '-') }}</span>
                                    </td>
                                    <td>
                                        <span class="log-chip" :class="levelBadgeClass(item.level)">{{ item.level || '-' }}</span>
                                    </td>
                                    <td>
                                        <span :class="['log-chip', statusBadgeClass(item.status)]">
                                            {{ getStatusText(item.status) }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="log-summary-clamp">{{ item.extra || '-' }}</span>
                                    </td>
                                    <td>
                                        <span class="font-mono whitespace-nowrap">{{ item.created_at || '-' }}</span>
                                    </td>
                                    <td>
                                        <button
                                            v-if="canViewDetail(item.action)"
                                            class="log-link-button"
                                            type="button"
                                            @click.stop="handleRedirect(item)"
                                        >{{ $tt('详情') }}</button>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <footer class="log-pagination">
                            <div>{{ $tt('共 {total} 条记录，第 {page} 页', { total: totalRecords, page: currentPage }) }}</div>
                            <div class="log-pagination__controls">
                                <div class="join">
                                    <button class="join-item btn btn-sm" type="button" :disabled="currentPage === 1" @click="handlePageChange(currentPage - 1)">«</button>
                                    <button class="join-item btn btn-sm" type="button">{{ currentPage }}</button>
                                    <button class="join-item btn btn-sm" type="button" :disabled="!hasNextPage" @click="handlePageChange(currentPage + 1)">»</button>
                                </div>
                                <input
                                    type="number"
                                    class="input input-bordered input-sm w-24"
                                    v-model.number="jumpPage"
                                    min="1"
                                    :placeholder="$tt('页码')"
                                    @keyup.enter="handlePageChange(Number(jumpPage))"
                                >
                                <button class="btn btn-sm btn-primary whitespace-nowrap" type="button" @click="handlePageChange(Number(jumpPage))">{{ $tt('跳转') }}</button>
                            </div>
                        </footer>
                    </div>
                </section>

                <section v-else class="log-panel log-panel--runtime">
                    <header class="log-toolbar">
                        <div class="log-toolbar__copy">
                            <h2>{{ activeRuntimeLabel }}</h2>
                            <p>{{ activeRuntimeDescription }}</p>
                            <p class="log-runtime-note">{{ $tt('root用户的日志重定向到docker的logs输出里，请查看docker的logs输出。') }}</p>
                        </div>
                        <div class="log-runtime-actions">
                            <label class="log-field">
                                <span>{{ $tt('轮询间隔') }}</span>
                                <span class="join w-full">
                                    <input type="number" class="join-item input input-bordered input-sm w-full" v-model.number="pollInterval" min="1" max="60">
                                    <span class="join-item btn btn-sm btn-disabled">{{ $tt('秒') }}</span>
                                </span>
                            </label>
                            <button :class="['btn btn-sm whitespace-nowrap', isPolling ? 'btn-outline btn-error' : 'btn-outline btn-success']" type="button" @click="togglePolling">
                                {{ isPolling ? $tt('停止') : $tt('开始') }}
                            </button>
                            <button class="btn btn-sm btn-primary whitespace-nowrap" type="button" @click="openDownloadDialog">
                                {{ $tt('反馈日志') }}
                            </button>
                        </div>
                    </header>

                    <div v-if="runtimeLogError" class="log-state log-state--error">
                        <strong>{{ $tt('提交失败') }}</strong>
                        <span>{{ runtimeLogError }}</span>
                    </div>

                    <div v-if="isRuntimeLoading" class="log-runtime-loading">{{ $tt('等待运行日志...') }}</div>

                    <div class="log-runtime-grid">
                        <section class="log-console">
                            <header>
                                <strong>{{ $tt('运行日志') }}</strong>
                                <span>{{ isPolling ? $tt('轮询中') : $tt('已暂停') }}</span>
                            </header>
                            <textarea
                                ref="logTextarea"
                                class="textarea log-textarea"
                                :placeholder="$tt('等待运行日志...')"
                                v-model="runtimeText"
                                readonly
                            ></textarea>
                        </section>

                        <section class="log-console log-console--error">
                            <header>
                                <strong>{{ $tt('错误日志') }}</strong>
                                <span>{{ activeRuntimeLabel }}</span>
                            </header>
                            <textarea
                                ref="failLogTextarea"
                                class="textarea log-textarea"
                                :placeholder="$tt('等待错误日志...')"
                                v-model="runtimeTextFail"
                                readonly
                            ></textarea>
                        </section>
                    </div>
                </section>
            </main>
        </div>

        <div class="drawer drawer-end">
            <input id="log-summary-drawer" type="checkbox" class="drawer-toggle" v-model="isLogDrawerOpen" />
            <div class="drawer-content"></div>
            <div class="drawer-side z-40">
                <label for="log-summary-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="log-detail-drawer min-h-full w-full max-w-xl p-5 md:p-6">
                    <div class="flex items-start justify-between gap-4 border-b border-base-300/80 pb-4">
                        <div>
                            <div class="text-xs font-medium text-base-content/50">{{ $tt('日志详情') }}</div>
                            <h3 class="mt-2 text-xl font-semibold text-base-content">
                                #{{ selectedLog.id || '-' }} / {{ $tt(selectedLog.action || '-') }}
                            </h3>
                            <div class="mt-2 text-sm text-base-content/55">
                                {{ selectedLog.user || '-' }} / {{ selectedLog.created_at || '-' }}
                            </div>
                        </div>
                        <button class="btn btn-sm btn-ghost" type="button" @click="closeLogDrawer">{{ $tt('关闭') }}</button>
                    </div>

                    <div class="space-y-5 pt-6">
                        <div class="grid gap-4 md:grid-cols-2">
                            <div class="log-drawer-card">
                                <div class="text-xs text-base-content/45">{{ $tt('等级') }}</div>
                                <div class="mt-2">
                                    <span class="log-chip" :class="levelBadgeClass(selectedLog.level)">
                                        {{ selectedLog.level || '-' }}
                                    </span>
                                </div>
                            </div>
                            <div class="log-drawer-card">
                                <div class="text-xs text-base-content/45">{{ $tt('状态') }}</div>
                                <div class="mt-2">
                                    <span :class="['log-chip', statusBadgeClass(selectedLog.status)]">
                                        {{ getStatusText(selectedLog.status) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="log-drawer-card">
                            <div class="text-sm font-semibold mb-3">{{ $tt('日志详情') }}</div>
                            <div class="text-sm leading-7 whitespace-pre-wrap break-words text-base-content/78">
                                {{ selectedLog.extra || '-' }}
                            </div>
                        </div>

                        <div class="log-drawer-card">
                            <div class="text-sm font-semibold mb-3">{{ $tt('基础信息') }}</div>
                            <div class="grid gap-3 md:grid-cols-2 text-sm">
                                <div>
                                    <div class="text-base-content/45">{{ $tt('ID') }}</div>
                                    <div class="mt-1 font-mono">#{{ selectedLog.id || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('用户') }}</div>
                                    <div class="mt-1">{{ selectedLog.user || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('操作类型') }}</div>
                                    <div class="mt-1">{{ $tt(selectedLog.action || '-') }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('创建时间') }}</div>
                                    <div class="mt-1">{{ selectedLog.created_at || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('结束时间') }}</div>
                                    <div class="mt-1">{{ selectedLog.complete_at || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('任务标识') }}</div>
                                    <div class="mt-1 font-mono break-all">{{ selectedLog.task_id || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('选择模式') }}</div>
                                    <div class="mt-1">{{ selectedLog.select_mode || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('状态原值') }}</div>
                                    <div class="mt-1 font-mono">{{ selectedLog.status || '-' }}</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="selectedLog.extra" class="log-drawer-card">
                            <div class="flex items-center justify-between gap-3">
                                <div class="text-sm font-semibold">{{ $tt('原始内容') }}</div>
                                <div class="log-chip log-chip--outline">{{ extraFormatLabel(selectedLog.extra) }}</div>
                            </div>
                            <pre class="log-pre">{{ formatExtraPayload(selectedLog.extra) }}</pre>
                        </div>

                        <div v-if="canViewDetail(selectedLog.action)" class="flex justify-end">
                            <button class="btn btn-primary" type="button" @click="handleRedirect(selectedLog)">
                                {{ $tt('进入详情页') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <dialog id="download_log" ref="download_log" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box">
                <h3 class="text-lg font-bold mb-4">{{ $tt('反馈日志') }}</h3>
                <div class="form-control mb-4">
                    <label class="label">
                        <span class="label-text">{{ $tt('反馈内容') }}</span>
                    </label>
                    <textarea
                        class="textarea textarea-bordered h-32"
                        :placeholder="$tt('请详细描述您遇到的问题，这将帮助开发者快速定位和解决问题（至少20字）')"
                        v-model="reason"
                    ></textarea>
                </div>
                <div class="alert alert-info mb-4">
                    <span class="text-sm">{{ $tt('系统会同时上传相关日志文件给开发者进行分析') }}</span>
                </div>
                <div class="modal-action">
                    <form method="dialog" class="flex space-x-2">
                        <button class="btn btn-primary" type="button" @click="handleUploadLog">
                            {{ $tt('提交反馈') }}
                        </button>
                        <button class="btn">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { createTaskLogWs } from '@/utils/syncRoomWs'

const DETAIL_ACTION_ALIASES = {
    importLibrary: ['导入收藏', 'import library', 'import_library', 'import-music'],
    autoScrape: ['自动刮削', 'auto scrape', 'auto_scrape', 'auto-tag'],
    backendScrape: ['后台刮削', 'backend scrape', 'backend_scrape'],
    duplicateCheck: ['重复检查', 'duplicate check', 'duplicate_check'],
    manualEdit: ['手动修改', 'manual edit', 'manual_edit'],
    tidyFolder: ['整理文件夹', 'tidy folder', 'tidy_folder'],
    scrapeArtist: ['刮削艺术家', 'scrape artist', 'scrape_artist'],
    removeEmptyFolder: ['删除空文件夹', 'remove empty folder', 'remove_empty_folder'],
    splitText: ['拆分文本', 'split text', 'split_text'],
    replaceText: ['替换文本', 'replace text', 'replace_text'],
    formatConvert: ['格式转换', 'format convert', 'format_convert'],
    splitTrack: ['切割音轨', 'split track', 'split_track'],
    garbledFix: ['乱码修复', 'garbled fix', 'garbled_fix'],
    replayGain: ['回放增益', 'replay gain', 'replay_gain'],
    generateStrm: ['生成strm文件', 'generate strm file', 'generate_strm_file', 'generate_strm'],
    deleteLibrary: ['删除收藏', 'delete library', 'delete_library'],
    mergeArtists: ['合并相似艺术家', 'merge similar artists', 'merge_artists'],
    aiCandidate: ['AI候选', 'ai candidate', 'ai_candidate'],
    operationPreset: ['快捷指令', 'operation preset', 'operation_preset']
}

const ACTION_META = {
    importLibrary: { badge: 'Import', tone: 'info', description: '导入收藏相关的执行记录与异常信息。' },
    autoScrape: { badge: 'Auto', tone: 'success', description: '自动刮削任务的执行结果与状态变化。' },
    backendScrape: { badge: 'Queue', tone: 'accent', description: '后台异步刮削任务的运行记录。' },
    duplicateCheck: { badge: 'Check', tone: 'warning', description: '重复检查流程中的比对结果和处理记录。' },
    manualEdit: { badge: 'Edit', tone: 'info', description: '人工编辑元数据后的变更日志。' },
    tidyFolder: { badge: 'Folder', tone: 'neutral', description: '整理文件夹流程的执行记录。' },
    scrapeArtist: { badge: 'Artist', tone: 'accent', description: '艺术家刮削相关的任务和处理结果。' },
    removeEmptyFolder: { badge: 'Clean', tone: 'warning', description: '删除空文件夹的清理任务记录。' },
    splitText: { badge: 'Split', tone: 'neutral', description: '拆分文本类操作的处理日志。' },
    replaceText: { badge: 'Replace', tone: 'neutral', description: '替换文本规则执行后的记录。' },
    formatConvert: { badge: 'Format', tone: 'info', description: '格式转换流程中的操作和异常。' },
    splitTrack: { badge: 'Track', tone: 'accent', description: '切割音轨任务的处理记录。' },
    garbledFix: { badge: 'Fix', tone: 'warning', description: '乱码修复流程的结果与状态。' },
    replayGain: { badge: 'Gain', tone: 'info', description: '回放增益计算和写入相关日志。' },
    generateStrm: { badge: 'STRM', tone: 'success', description: '生成 strm 文件任务的执行记录。' },
    deleteLibrary: { badge: 'Delete', tone: 'error', description: '删除收藏操作的执行结果。' },
    mergeArtists: { badge: 'Merge', tone: 'accent', description: '合并相似艺术家任务的处理日志。' },
    aiCandidate: { badge: 'AI', tone: 'success', description: 'AI 候选相关的判断与处理记录。' }
}

const RUNTIME_OPTIONS = [
    {
        value: 'gunicorn',
        label: 'Gunicorn',
        badge: 'Web',
        tone: 'success',
        description: 'Web 进程日志，主要用于排查接口请求和服务异常。'
    },
    {
        value: 'worker',
        label: 'Worker',
        badge: 'Async',
        tone: 'accent',
        description: '异步任务执行日志，适合查看 Celery 后台处理过程。'
    },
    {
        value: 'beat',
        label: 'Beat',
        badge: 'Scheduler',
        tone: 'info',
        description: '定时任务调度日志，用于检查计划任务是否正常触发。'
    },
    {
        value: 'redis',
        label: 'Redis',
        badge: 'Cache',
        tone: 'warning',
        description: '缓存与队列服务日志，适合定位连接与缓存层异常。'
    },
    {
        value: 'xiaoai',
        label: 'Xiaoai',
        badge: 'Device',
        tone: 'neutral',
        description: '小爱相关服务日志，用于检查设备联动和指令处理。'
    }
]

export default {
    name: 'Log',
    data() {
        return {
            size: 'small',
            auditLogData: [],
            tabSelectRuntime: 'gunicorn',
            tabSelect2: 'log',
            runtimeText: '',
            runtimeTextFail: '',
            pollInterval: 5,
            isPolling: true,
            pollingTimer: null,
            currentPage: 1,
            hasNextPage: true,
            jumpPage: 1,
            reason: '',
            totalRecords: 0,
            levelFilter: '',
            statusFilter: '',
            userFilter: '',
            isLogDrawerOpen: false,
            selectedLog: {},
            actionStatsCache: {},
            isAuditLoading: false,
            isRuntimeLoading: false,
            auditLogError: '',
            runtimeLogError: '',
            taskLogWs: null,
            taskLogReconnectTimer: null,
            taskLogRefreshTimer: null,
            isDestroyingTaskLogWs: false
        }
    },
    computed: {
        ...mapGetters(['getUserRole']),
        tabSelect: {
            get() {
                return this.$store.state.log.tabSelect
            },
            set(value) {
                this.$store.dispatch('log/setTabSelect', value)
            }
        },
        actionOptions() {
            return [
                {
                    key: 'all',
                    value: '',
                    aliases: [],
                    label: '全部操作',
                    badge: 'All',
                    tone: 'neutral',
                    description: '汇总展示全部任务行为，适合排查最近的整体执行情况。'
                },
                ...Object.entries(DETAIL_ACTION_ALIASES).map(([key, aliases]) => {
                    const meta = ACTION_META[key] || {}
                    return {
                        key,
                        value: aliases[0],
                        aliases,
                        label: aliases[0],
                        badge: meta.badge || 'Log',
                        tone: meta.tone || 'neutral',
                        description: meta.description || '当前分类包含该类型的操作记录。'
                    }
                })
            ]
        },
        visibleActionOptions() {
            return this.actionOptions.filter((item) => {
                if (item.key === 'all') {
                    return true
                }
                return this.getActionCount(item.key) > 0
            })
        },
        currentActionKey() {
            if (!this.tabSelect) {
                return 'all'
            }
            const exactMatch = this.actionOptions.find((item) => item.key === this.tabSelect)
            if (exactMatch) {
                return exactMatch.key
            }
            const legacyMatch = this.actionOptions.find((item) => item.aliases.includes(this.tabSelect))
            return legacyMatch ? legacyMatch.key : 'all'
        },
        runtimeOptions() {
            return RUNTIME_OPTIONS
        },
        activeActionOption() {
            return this.actionOptions.find((item) => item.key === this.currentActionKey) || this.actionOptions[0]
        },
        activeRuntimeOption() {
            return this.runtimeOptions.find((item) => item.value === this.tabSelectRuntime) || this.runtimeOptions[0]
        },
        activeModeTitle() {
            return this.tabSelect2 === 'log' ? this.$tt('操作日志概览') : this.$tt('运行日志面板')
        },
        activeModeBadge() {
            return this.tabSelect2 === 'log' ? this.$tt('Log Stream') : this.$tt('Runtime Stream')
        },
        modeBadgeClass() {
            return this.tabSelect2 === 'log' ? 'bg-info/10 text-info' : 'bg-success/10 text-success'
        },
        summaryPrimaryText() {
            if (this.tabSelect2 === 'log') {
                return this.$tt('共 {count} 条', { count: this.totalRecords })
            }
            return this.$tt('服务 {name}', { name: this.activeRuntimeOption.label })
        },
        summarySecondaryText() {
            if (this.tabSelect2 === 'log') {
                return this.$tt('当前页 {page} / 本页 {count} 条', { page: this.currentPage, count: this.auditLogData.length })
            }
            return this.isPolling ? this.$tt('轮询 {sec} 秒', { sec: this.pollInterval }) : this.$tt('轮询已暂停')
        },
        summaryTertiaryText() {
            if (this.tabSelect2 === 'log') {
                return this.$tt('筛选 {text}', { text: this.filterSummaryText })
            }
            return this.$tt('错误面板已同步')
        },
        activeModeHint() {
            return this.tabSelect2 === 'log'
                ? this.$tt('左侧固定操作上下文，右侧只查看当前筛选下的日志列表。')
                : this.$tt('左侧切换服务，右侧同步查看运行输出与错误输出。')
        },
        runtimeSourceHint() {
            return this.$tt('服务源') + ': ' + this.activeRuntimeOption.label
        },
        activeActionLabel() {
            return this.$tt(this.activeActionOption.label)
        },
        activeActionDescription() {
            return this.$tt(this.activeActionOption.description)
        },
        activeRuntimeLabel() {
            return this.activeRuntimeOption.label
        },
        activeRuntimeDescription() {
            return this.$tt(this.activeRuntimeOption.description)
        },
        headerAccentWidth() {
            if (this.tabSelect2 === 'runtime') {
                return this.isPolling ? 100 : 42
            }
            if (!this.totalRecords) {
                return 18
            }
            return Math.max(18, Math.min(100, Math.round((this.currentPage / Math.max(1, Math.ceil(this.totalRecords / 50))) * 100)))
        },
        pageAccentBarClass() {
            return this.tabSelect2 === 'log' ? 'bg-info/25' : 'bg-success/25'
        },
        filterSummaryText() {
            const summary = []
            if (this.currentActionKey !== 'all') {
                summary.push(this.$tt(this.activeActionOption.label))
            }
            if (this.levelFilter) {
                summary.push(this.$tt(this.levelFilter === 'info' ? '信息' : this.levelFilter === 'warning' ? '警告' : '错误'))
            }
            if (this.statusFilter) {
                summary.push(this.getStatusText(this.statusFilter))
            }
            if (this.userFilter) {
                summary.push(this.userFilter)
            }
            return summary.length ? summary.join(' / ') : this.$tt('全部')
        }
    },
    created() {
        if (this.currentActionKey !== this.tabSelect) {
            this.tabSelect = this.currentActionKey === 'all' ? '' : this.currentActionKey
        }
        this.fetchAuditLog()
        this.connectTaskLogWs()
    },
    beforeDestroy() {
        this.isDestroyingTaskLogWs = true
        this.closeTaskLogWs()
        this.clearPollingTimer()
    },
    methods: {
        normalizeActionName(action) {
            return String(action || '')
                .trim()
                .toLowerCase()
                .replace(/[_-]/g, ' ')
                .replace(/\s+/g, ' ')
        },
        canViewDetail(action) {
            const normalizedAction = this.normalizeActionName(action)
            return Object.values(DETAIL_ACTION_ALIASES).some((aliases) =>
                aliases.some((alias) => this.normalizeActionName(alias) === normalizedAction)
            )
        },
        getStatusText(status) {
            const statusMap = {
                running: '进行中',
                success: '成功',
                stop: '停止',
                failed: '失败',
                wait: '等待'
            }
            return this.$tt(statusMap[status] || status || '-')
        },
        statusBadgeClass(status) {
            const classMap = {
                running: 'bg-info/10 text-info',
                wait: 'bg-info/10 text-info',
                success: 'bg-success/10 text-success',
                stop: 'bg-warning/20 text-warning',
                failed: 'bg-error/10 text-error'
            }
            return classMap[status] || 'bg-base-200 text-base-content/65'
        },
        levelBadgeClass(level) {
            const classMap = {
                info: 'bg-base-200 text-base-content/70',
                warning: 'bg-warning/20 text-warning',
                error: 'bg-error/10 text-error'
            }
            return classMap[level] || 'bg-base-200 text-base-content/65'
        },
        navRowClass(active) {
            return active ? 'bg-info/10 ring-1 ring-info/30' : 'hover:bg-base-200/70'
        },
        softBadgeClass(tone) {
            const map = {
                success: 'bg-success/10 text-success',
                info: 'bg-info/10 text-info',
                warning: 'bg-warning/20 text-warning',
                error: 'bg-error/10 text-error',
                accent: 'bg-accent/10 text-accent',
                neutral: 'bg-base-200 text-base-content/70',
                ghost: 'bg-base-200 text-base-content/60'
            }
            return map[tone] || map.neutral
        },
        selectAction(actionKey) {
            this.tabSelect = actionKey === 'all' ? '' : actionKey
            this.currentPage = 1
            this.jumpPage = 1
            this.fetchAuditLog()
        },
        selectRuntime(value) {
            this.tabSelectRuntime = value
            this.fetchRuntimeLog()
        },
        handlePageChange(page) {
            const targetPage = Number(page)
            if (!targetPage || targetPage < 1) {
                return
            }
            this.currentPage = targetPage
            this.jumpPage = targetPage
            this.fetchAuditLog()
        },
        fetchAuditLog() {
            this.isAuditLoading = true
            this.auditLogError = ''
            const params = {
                page: this.currentPage,
                page_size: 50
            }

            if (this.currentActionKey !== 'all') params.action_group = this.currentActionKey
            if (this.levelFilter) params.level = this.levelFilter
            if (this.statusFilter) params.status = this.statusFilter
            if (this.userFilter) params.user = this.userFilter

            this.$api.Task.getAuditLog(params).then(res => {
                if (res.result) {
                    this.auditLogData = res.data.items
                    this.hasNextPage = res.data.count > this.currentPage * 50
                    this.totalRecords = res.data.count
                    this.actionStatsCache = Object.entries(res.data.action_stats || {}).reduce((acc, [key, count]) => {
                        acc[key] = { count }
                        return acc
                    }, {})
                } else {
                    this.auditLogError = this.$tt('网络错误或服务器异常')
                }
            }).catch(() => {
                this.auditLogError = this.$tt('网络错误或服务器异常')
            }).finally(() => {
                this.isAuditLoading = false
            })
        },
        connectTaskLogWs() {
            this.closeTaskLogWs()
            this.taskLogWs = createTaskLogWs(null, {
                onClose: () => {
                    if (this.isDestroyingTaskLogWs) {
                        return
                    }
                    this.taskLogReconnectTimer = setTimeout(() => {
                        this.connectTaskLogWs()
                    }, 2000)
                },
                onMessage: (msg) => {
                    this.handleTaskLogWsMessage(msg)
                }
            })
        },
        closeTaskLogWs() {
            if (this.taskLogReconnectTimer) {
                clearTimeout(this.taskLogReconnectTimer)
                this.taskLogReconnectTimer = null
            }
            if (this.taskLogRefreshTimer) {
                clearTimeout(this.taskLogRefreshTimer)
                this.taskLogRefreshTimer = null
            }
            if (this.taskLogWs) {
                this.taskLogWs.close()
                this.taskLogWs = null
            }
        },
        handleTaskLogWsMessage(msg) {
            if (!msg || !msg.event || msg.event === 'task_log.connected') {
                return
            }
            if (!msg.event.startsWith('audit_log.')) {
                return
            }
            if (this.taskLogRefreshTimer) {
                clearTimeout(this.taskLogRefreshTimer)
            }
            this.taskLogRefreshTimer = setTimeout(() => {
                this.fetchAuditLog()
            }, 500)
        },
        fetchRuntimeLog() {
            this.isRuntimeLoading = true
            this.runtimeLogError = ''
            this.$api.Task.getRuntimeLog({ type: this.tabSelectRuntime }).then(res => {
                if (res.result) {
                    this.runtimeText = res.data.log
                    this.runtimeTextFail = res.data.fail_log
                    this.$nextTick(() => {
                        if (this.$refs.logTextarea) {
                            this.$refs.logTextarea.scrollTop = this.$refs.logTextarea.scrollHeight
                        }
                        if (this.$refs.failLogTextarea) {
                            this.$refs.failLogTextarea.scrollTop = this.$refs.failLogTextarea.scrollHeight
                        }
                    })
                } else {
                    this.runtimeLogError = this.$tt('网络错误或服务器异常')
                }
            }).catch(() => {
                this.runtimeLogError = this.$tt('网络错误或服务器异常')
            }).finally(() => {
                this.isRuntimeLoading = false
            })
        },
        handleFilterChange() {
            this.currentPage = 1
            this.jumpPage = 1
            this.fetchAuditLog()
        },
        resetFilters() {
            this.tabSelect = ''
            this.levelFilter = ''
            this.statusFilter = ''
            this.userFilter = ''
            this.currentPage = 1
            this.jumpPage = 1
            this.fetchAuditLog()
        },
        handleTab2(v) {
            if (this.tabSelect2 === v) {
                return
            }
            this.tabSelect2 = v
            if (v === 'log') {
                this.clearPollingTimer()
                this.currentPage = 1
                this.jumpPage = 1
                this.fetchAuditLog()
            } else {
                this.fetchRuntimeLog()
                if (this.isPolling) {
                    this.startPolling()
                }
            }
        },
        handleRedirect(item) {
            if (this.getUserRole === 'V2') {
                const targetPath = item.status === 'running'
                    ? '/task_progress/' + item.id + '/'
                    : '/log_detail/' + item.id + '/'
                this.$router.push({ path: targetPath })
            } else {
                this.$myMsg.notify({
                    title: this.$tt('权限失败'),
                    content: this.$tt('V1 版本：您没有该功能的操作权限'),
                    type: 'error',
                    time: 3000
                })
            }
        },
        openLogDrawer(item) {
            this.selectedLog = { ...item }
            this.isLogDrawerOpen = true
        },
        closeLogDrawer() {
            this.isLogDrawerOpen = false
        },
        getUserInitial(user) {
            const value = String(user || '').trim()
            return value ? value.charAt(0).toUpperCase() : '?'
        },
        formatCreatedTime(createdAt) {
            if (!createdAt) {
                return '-'
            }
            const parts = String(createdAt).split(' ')
            return parts.length > 1 ? parts[1] : parts[0]
        },
        getActionCount(actionKey) {
            const stats = this.actionStatsCache[actionKey]
            return stats ? stats.count : 0
        },
        getActionCountValue(actionKey) {
            const stats = this.actionStatsCache[actionKey]
            if (!stats) {
                return '--'
            }
            return stats.count
        },
        getActionCountLabel(actionKey) {
            const stats = this.actionStatsCache[actionKey]
            if (!stats) {
                return this.$tt('未加载')
            }
            return this.$tt('总数')
        },
        formatExtraPayload(value) {
            if (value === null || value === undefined || value === '') {
                return '-'
            }
            if (typeof value === 'object') {
                try {
                    return JSON.stringify(value, null, 2)
                } catch (error) {
                    return String(value)
                }
            }
            const text = String(value)
            try {
                return JSON.stringify(JSON.parse(text), null, 2)
            } catch (error) {
                return text
            }
        },
        extraFormatLabel(value) {
            if (value && typeof value === 'object') {
                return 'JSON'
            }
            try {
                JSON.parse(String(value || ''))
                return 'JSON'
            } catch (error) {
                return 'TEXT'
            }
        },
        openDownloadDialog() {
            if (this.$refs.download_log && typeof this.$refs.download_log.showModal === 'function') {
                this.$refs.download_log.showModal()
            }
        },
        togglePolling() {
            if (this.isPolling) {
                this.stopPolling()
            } else {
                this.startPolling()
            }
        },
        startPolling() {
            this.clearPollingTimer()
            this.isPolling = true
            this.fetchRuntimeLog()
            this.pollingTimer = setInterval(() => {
                this.fetchRuntimeLog()
            }, Math.max(1, Number(this.pollInterval) || 5) * 1000)
        },
        stopPolling() {
            this.isPolling = false
            this.clearPollingTimer()
        },
        clearPollingTimer() {
            if (this.pollingTimer) {
                clearInterval(this.pollingTimer)
                this.pollingTimer = null
            }
        },
        handleUploadLog() {
            if (this.reason.length < 20) {
                this.$myMsg.notify({
                    title: this.$tt('反馈失败！'),
                    content: this.$tt('反馈理由至少20字'),
                    type: 'error',
                    time: 3000
                })
                return
            }
            this.$api.Task.uploadLog({
                reason: this.reason
            }).then(res => {
                if (res.result) {
                    this.$refs.download_log.close()
                    this.reason = ''
                    this.$myMsg.notify({
                        title: this.$tt('反馈成功！'),
                        type: 'success',
                        time: 3000
                    })
                }
            })
        },
        handleDeleteAuditLogs() {
            this.$api.Task.createPeriodicTask({ name: 'audit_log', type: 'manual', enabled: true }).then(res => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('删除任务已提交'),
                        content: this.$tt('日志正在后台删除中，请稍后刷新查看'),
                        type: 'success',
                        time: 3000
                    })
                }
            }).catch(() => {
                this.$myMsg.notify({
                    title: this.$tt('提交失败'),
                    content: this.$tt('网络错误或服务器异常'),
                    type: 'error',
                    time: 3000
                })
            })
        }
    }
}
</script>

<style scoped>
.log-page {
    min-height: calc(100dvh - 4.5rem);
    padding: 0.5rem;
    color: oklch(var(--bc));
    background: oklch(var(--b2));
}

.log-shell {
    display: grid;
    width: 100%;
    max-width: 1480px;
    grid-template-columns: 17rem minmax(0, 1fr);
    min-height: calc(100dvh - 5.5rem);
    overflow: hidden;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 18px;
    background:
        radial-gradient(circle at 12% 0%, oklch(var(--p) / 0.08), transparent 28rem),
        oklch(var(--b1));
}

.log-sidebar {
    min-height: 0;
    overflow-y: auto;
    border-right: 1px solid oklch(var(--bc) / 0.1);
    background: color-mix(in srgb, oklch(var(--b2)) 72%, transparent);
    padding: 0.875rem;
}

.log-mode-switch {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 14px;
    background: oklch(var(--b1) / 0.72);
    padding: 0.25rem;
}

.log-mode-switch__item {
    min-height: 2.25rem;
    border-radius: 10px;
    color: oklch(var(--bc) / 0.62);
    font-size: 0.875rem;
    font-weight: 650;
    transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.log-mode-switch__item:hover,
.log-mode-switch__item.is-active {
    background: oklch(var(--bc) / 0.1);
    color: oklch(var(--bc) / 0.94);
}

.log-mode-switch__item:active,
.log-nav-row:active {
    transform: translateY(1px);
}

.log-nav-list {
    display: grid;
    gap: 0.375rem;
    margin-top: 0.875rem;
}

.log-nav-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 3rem;
    border: 1px solid transparent;
    border-radius: 14px;
    padding: 0.625rem 0.75rem;
    text-align: left;
    transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.log-nav-row:hover,
.log-nav-row.is-active {
    border-color: oklch(var(--p) / 0.18);
    background: oklch(var(--p) / 0.09);
}

.log-nav-row__main {
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
}

.log-nav-row__label {
    overflow: hidden;
    color: oklch(var(--bc) / 0.9);
    font-size: 0.875rem;
    font-weight: 650;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.log-nav-row__badge,
.log-chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    border-radius: 999px;
    padding: 0.18rem 0.55rem;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.25;
    white-space: nowrap;
}

.log-nav-row__count {
    color: oklch(var(--bc) / 0.72);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.875rem;
    font-weight: 700;
}

.log-main {
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem;
}

.log-panel {
    display: flex;
    min-height: 100%;
    flex-direction: column;
    gap: 0.875rem;
}

.log-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: start;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.78);
    padding: 1rem;
}

.log-toolbar__copy h2 {
    margin: 0;
    color: oklch(var(--bc) / 0.96);
    font-size: clamp(1.35rem, 2vw, 1.8rem);
    font-weight: 760;
    line-height: 1.15;
}

.log-toolbar__copy p {
    margin-top: 0.375rem;
    max-width: 58ch;
    color: oklch(var(--bc) / 0.58);
    font-size: 0.875rem;
    line-height: 1.65;
}

.log-toolbar__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(5.2rem, 1fr));
    gap: 0.5rem;
}

.log-stat {
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 14px;
    background: oklch(var(--b2) / 0.52);
    padding: 0.625rem 0.75rem;
}

.log-stat span,
.log-field span {
    display: block;
    color: oklch(var(--bc) / 0.54);
    font-size: 0.75rem;
    font-weight: 650;
}

.log-stat strong {
    display: block;
    margin-top: 0.25rem;
    color: oklch(var(--bc) / 0.94);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 1.15rem;
}

.log-filter-bar {
    display: grid;
    grid-template-columns: minmax(8rem, 0.85fr) minmax(8rem, 0.85fr) minmax(12rem, 1.2fr) auto;
    gap: 0.625rem;
    align-items: end;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: color-mix(in srgb, oklch(var(--b2)) 54%, transparent);
    padding: 0.75rem;
}

.log-field {
    min-width: 0;
}

.log-control {
    width: 100%;
    margin-top: 0.35rem;
    border-color: oklch(var(--bc) / 0.12);
    background: oklch(var(--b1) / 0.9);
    color: oklch(var(--bc) / 0.9);
}

.log-control:focus {
    outline: 2px solid oklch(var(--p) / 0.24);
    outline-offset: 1px;
}

.log-filter-actions,
.log-runtime-actions,
.log-pagination__controls {
    display: flex;
    align-items: end;
    gap: 0.5rem;
}

.log-table-wrap {
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.86);
}

.log-table {
    min-width: 1080px;
}

.log-table thead tr {
    background: oklch(var(--b2) / 0.68);
}

.log-table th {
    color: oklch(var(--bc) / 0.56);
    font-size: 0.72rem;
    font-weight: 760;
    letter-spacing: 0;
    white-space: nowrap;
}

.log-table td {
    color: oklch(var(--bc) / 0.74);
    font-size: 0.875rem;
    vertical-align: top;
}

.log-table__row {
    cursor: pointer;
    border-top: 1px solid oklch(var(--bc) / 0.08);
    transition: background-color 140ms ease;
}

.log-table__row:hover {
    background: oklch(var(--p) / 0.055);
}

.log-user {
    color: oklch(var(--bc) / 0.9);
    font-weight: 650;
}

.log-chip--outline {
    border: 1px solid oklch(var(--bc) / 0.14);
    background: transparent;
    color: oklch(var(--bc) / 0.68);
}

.log-link-button {
    display: inline-flex;
    align-items: center;
    min-height: 1.75rem;
    border-radius: 999px;
    background: oklch(var(--p) / 0.12);
    color: oklch(var(--p));
    font-size: 0.78rem;
    font-weight: 760;
    padding: 0.25rem 0.7rem;
    white-space: nowrap;
}

.log-link-button:hover {
    background: oklch(var(--p) / 0.18);
}

.log-summary-clamp {
    display: -webkit-box;
    max-width: 34rem;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
}

.log-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border-top: 1px solid oklch(var(--bc) / 0.1);
    padding: 0.75rem;
    color: oklch(var(--bc) / 0.58);
    font-size: 0.875rem;
}

.log-state {
    display: grid;
    place-items: center;
    min-height: 16rem;
    border: 1px dashed oklch(var(--bc) / 0.16);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.62);
    color: oklch(var(--bc) / 0.56);
    padding: 2rem;
    text-align: center;
}

.log-state strong {
    color: oklch(var(--bc) / 0.9);
    font-size: 1rem;
}

.log-state span {
    margin-top: 0.35rem;
}

.log-state--error {
    border-color: oklch(var(--er) / 0.28);
    background: oklch(var(--er) / 0.08);
}

.log-list-skeleton {
    display: grid;
    gap: 0.5rem;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.74);
    padding: 0.75rem;
}

.log-skeleton-row {
    height: 3.25rem;
    border-radius: 12px;
    background: linear-gradient(
        90deg,
        oklch(var(--b2) / 0.62),
        oklch(var(--b3) / 0.42),
        oklch(var(--b2) / 0.62)
    );
    background-size: 200% 100%;
}

.log-panel--runtime {
    min-height: calc(100dvh - 7.5rem);
}

.log-runtime-note {
    color: oklch(var(--wa) / 0.88) !important;
}

.log-runtime-actions {
    align-items: end;
}

.log-runtime-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.875rem;
    flex: 1;
    min-height: 28rem;
}

.log-console {
    display: flex;
    min-height: 0;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.84);
}

.log-console header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border-bottom: 1px solid oklch(var(--bc) / 0.1);
    background: oklch(var(--su) / 0.08);
    padding: 0.75rem 0.875rem;
}

.log-console--error header {
    background: oklch(var(--er) / 0.08);
}

.log-console header strong {
    color: oklch(var(--bc) / 0.92);
}

.log-console header span,
.log-runtime-loading {
    color: oklch(var(--bc) / 0.52);
    font-size: 0.8125rem;
}

.log-runtime-loading {
    margin-top: -0.25rem;
}

.log-textarea {
    width: 100%;
    flex: 1;
    min-height: 24rem;
    resize: none;
    border: 0;
    border-radius: 0;
    background: color-mix(in srgb, oklch(var(--b2)) 64%, transparent);
    color: oklch(var(--bc) / 0.84);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.8125rem;
    line-height: 1.65;
}

.log-detail-drawer {
    background: oklch(var(--b1));
    box-shadow: -18px 0 48px oklch(var(--bc) / 0.18);
}

.log-drawer-card {
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: oklch(var(--b2) / 0.42);
    padding: 1rem;
}

.log-pre {
    margin-top: 1rem;
    max-height: 22rem;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    border-radius: 12px;
    background: oklch(var(--b1) / 0.86);
    color: oklch(var(--bc) / 0.76);
    padding: 0.875rem;
    font-size: 0.75rem;
    line-height: 1.6;
}

.log-sidebar,
.log-main,
.log-textarea,
.log-pre {
    scrollbar-width: thin;
    scrollbar-color: oklch(var(--bc) / 0.18) transparent;
}

.log-sidebar::-webkit-scrollbar,
.log-main::-webkit-scrollbar,
.log-textarea::-webkit-scrollbar,
.log-pre::-webkit-scrollbar {
    width: 0.45rem;
    height: 0.45rem;
}

.log-sidebar::-webkit-scrollbar-thumb,
.log-main::-webkit-scrollbar-thumb,
.log-textarea::-webkit-scrollbar-thumb,
.log-pre::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: oklch(var(--bc) / 0.18);
}

.log-page {
    min-height: calc(100dvh - 4.5rem);
    padding: 16px;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 12% 8%, oklch(var(--p) / 0.08), transparent 24rem),
        radial-gradient(circle at 90% 0%, oklch(var(--a) / 0.06), transparent 18rem),
        oklch(var(--b2));
}

.log-hero {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(240px, 0.95fr);
    gap: 14px;
    width: min(100%, 1480px);
    margin: 0 auto 12px;
    padding: 18px;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background:
        radial-gradient(circle at 88% 18%, oklch(var(--p) / 0.08), transparent 18rem),
        linear-gradient(110deg, oklch(var(--b1) / 0.98), oklch(var(--b2) / 0.82));
    box-shadow:
        0 18px 48px oklch(var(--bc) / 0.08),
        inset 0 1px 0 oklch(var(--b1) / 0.52);
}

.log-hero__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    justify-content: center;
}

.log-hero__eyebrow {
    width: fit-content;
    margin-bottom: 12px;
    padding: 5px 10px;
    border-radius: 999px;
    color: oklch(var(--p));
    background: oklch(var(--p) / 0.12);
    font-size: 12px;
    font-weight: 700;
}

.log-hero h1 {
    margin: 0;
    color: oklch(var(--bc));
    font-size: clamp(28px, 3.4vw, 44px);
    font-weight: 800;
    line-height: 1.08;
}

.log-hero p {
    max-width: 58ch;
    margin: 12px 0 0;
    color: oklch(var(--bc) / 0.62);
    font-size: 14px;
    line-height: 1.7;
}

.log-hero__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
}

.log-hero__stat {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
    box-shadow: inset 0 1px 0 oklch(var(--b1) / 0.45);
}

.log-hero__stat span {
    color: oklch(var(--bc) / 0.56);
    font-size: 12px;
}

.log-hero__stat strong {
    overflow: hidden;
    color: oklch(var(--bc));
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    font-weight: 800;
}

.log-hero__stat small {
    overflow: hidden;
    color: oklch(var(--bc) / 0.54);
    font-size: 12px;
    line-height: 1.6;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.log-shell {
    display: grid;
    width: 100%;
    max-width: 1480px;
    grid-template-columns: 17rem minmax(0, 1fr);
    min-height: calc(100dvh - 12rem);
    overflow: hidden;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.92);
    box-shadow: 0 18px 48px oklch(var(--bc) / 0.08);
}

.log-sidebar {
    min-height: 0;
    overflow-y: auto;
    border-right: 1px solid oklch(var(--bc) / 0.08);
    background: linear-gradient(180deg, oklch(var(--b2) / 0.78), oklch(var(--b1) / 0.92));
    padding: 14px;
}

.log-mode-switch {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 999px;
    background: oklch(var(--b1) / 0.76);
    padding: 4px;
}

.log-mode-switch__item {
    min-height: 34px;
    border-radius: 999px;
    color: oklch(var(--bc) / 0.64);
    font-size: 0.875rem;
    font-weight: 700;
    transition: background-color 160ms ease, color 160ms ease, transform 160ms ease;
}

.log-mode-switch__item:hover,
.log-mode-switch__item.is-active {
    background: oklch(var(--p) / 0.1);
    color: oklch(var(--bc));
}

.log-nav-list {
    display: grid;
    gap: 0.375rem;
    margin-top: 12px;
}

.log-nav-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-height: 3rem;
    border: 1px solid transparent;
    border-radius: 14px;
    padding: 0.625rem 0.75rem;
    text-align: left;
    transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.log-nav-row:hover,
.log-nav-row.is-active {
    border-color: oklch(var(--p) / 0.16);
    background: oklch(var(--p) / 0.08);
}

.log-main {
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
    padding: 14px;
}

.log-panel {
    display: flex;
    min-height: 100%;
    flex-direction: column;
    gap: 0.875rem;
}

.log-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: start;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.84);
    padding: 1rem;
}

.log-toolbar__copy h2 {
    margin: 0;
    color: oklch(var(--bc));
    font-size: clamp(1.35rem, 2vw, 1.8rem);
    font-weight: 800;
    line-height: 1.15;
}

.log-toolbar__copy p {
    margin-top: 0.4rem;
    max-width: 58ch;
    color: oklch(var(--bc) / 0.6);
    font-size: 0.875rem;
    line-height: 1.65;
}

.log-toolbar__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(5.2rem, 1fr));
    gap: 0.5rem;
}

.log-stat {
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 14px;
    background: oklch(var(--b2) / 0.54);
    padding: 0.625rem 0.75rem;
}

.log-filter-bar {
    display: grid;
    grid-template-columns: minmax(8rem, 0.85fr) minmax(8rem, 0.85fr) minmax(12rem, 1.2fr) auto;
    gap: 0.625rem;
    align-items: end;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background: color-mix(in srgb, oklch(var(--b2)) 54%, transparent);
    padding: 0.75rem;
}

.log-control {
    width: 100%;
    margin-top: 0.35rem;
    border-color: oklch(var(--bc) / 0.12);
    background: oklch(var(--b1) / 0.9);
    color: oklch(var(--bc) / 0.9);
}

.log-table-wrap,
.log-list-skeleton,
.log-state,
.log-console,
.log-drawer-card {
    border-radius: 16px;
}

.log-table-wrap {
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.08);
    background: oklch(var(--b1) / 0.9);
}

.log-table thead tr {
    background: oklch(var(--b2) / 0.7);
}

.log-table th {
    color: oklch(var(--bc) / 0.56);
}

.log-table__row:hover {
    background: oklch(var(--p) / 0.055);
}

.log-chip {
    border-radius: 999px;
}

.log-state {
    border: 1px dashed oklch(var(--bc) / 0.14);
    background: oklch(var(--b1) / 0.68);
}

.log-list-skeleton {
    border: 1px solid oklch(var(--bc) / 0.08);
    background: oklch(var(--b1) / 0.76);
    padding: 0.75rem;
}

.log-skeleton-row {
    border-radius: 12px;
}

.log-console {
    border: 1px solid oklch(var(--bc) / 0.08);
    background: oklch(var(--b1) / 0.88);
}

.log-console header {
    border-bottom: 1px solid oklch(var(--bc) / 0.08);
}

.log-textarea {
    background: color-mix(in srgb, oklch(var(--b2)) 64%, transparent);
}

.log-detail-drawer {
    background: oklch(var(--b1));
    box-shadow: -18px 0 48px oklch(var(--bc) / 0.14);
}

.log-drawer-card {
    border: 1px solid oklch(var(--bc) / 0.08);
    background: oklch(var(--b2) / 0.42);
}

.log-pre {
    background: oklch(var(--b1) / 0.86);
}

@media (prefers-reduced-motion: no-preference) {
    .log-skeleton-row {
        animation: log-shimmer 1.4s ease-in-out infinite;
    }
}

@keyframes log-shimmer {
    0% {
        background-position: 120% 0;
    }
    100% {
        background-position: -80% 0;
    }
}

@media (max-width: 1279px) {
    .log-shell {
        grid-template-columns: 15rem minmax(0, 1fr);
    }

    .log-toolbar,
    .log-filter-bar {
        grid-template-columns: 1fr;
    }

    .log-toolbar__stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 1023px) {
    .log-page {
        padding: 10px;
    }

    .log-hero {
        grid-template-columns: 1fr;
        padding: 16px;
    }

    .log-shell {
        grid-template-columns: 1fr;
        min-height: calc(100dvh - 8rem);
    }

    .log-sidebar {
        border-right: 0;
        border-bottom: 1px solid oklch(var(--bc) / 0.1);
    }

    .log-nav-list {
        grid-auto-flow: column;
        grid-auto-columns: minmax(11rem, 1fr);
        overflow-x: auto;
        padding-bottom: 0.125rem;
    }

    .log-nav-row {
        min-height: 2.8rem;
    }

    .log-main {
        padding: 10px;
    }

    .log-runtime-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .log-hero__stats {
        grid-template-columns: 1fr;
    }

    .log-toolbar__stats {
        grid-template-columns: 1fr;
    }

    .log-filter-actions,
    .log-runtime-actions,
    .log-pagination,
    .log-pagination__controls {
        align-items: stretch;
        flex-direction: column;
    }

    .log-runtime-actions .log-field {
        width: 100%;
    }

    .log-detail-drawer {
        max-width: 100%;
    }
}
</style>
